import type { IFilterOptions } from "@fluidattacks/design";
import type { UseBoundStore } from "zustand";
import type { StoreApi } from "zustand";
import type { StateStorage } from "zustand/middleware";
import { createJSONStorage, persist } from "zustand/middleware";
import { createWithEqualityFn } from "zustand/traditional";

import {
  formatChecks,
  getFilter,
} from "features/vulnerabilities/filters/utils";
import type {
  FindingFiltersInput,
  FindingLastReportInterval,
  FindingStatus,
  Technique,
  VulnerabilityOrigin,
  VulnerabilityTreatment,
} from "gql/graphql";
import { filtersStorage } from "storage/filters";
import type { TFlatten, TStoreValues } from "utils/zustand";

type TFilter = FindingFiltersInput;

const initialValues: TStoreValues<TFilter> = {
  assignees: undefined,
  autoFixable: undefined,
  lastReportAfter: undefined,
  lastReportBefore: undefined,
  lastReportInterval: undefined,
  maxPriority: undefined,
  maxSeverity: undefined,
  minPriority: undefined,
  minSeverity: undefined,
  origin: undefined,
  reattack: [],
  reportedAfter: undefined,
  reportedBefore: undefined,
  search: "",
  severityRating: [],
  state: [],
  tags: [],
  technique: [],
  title: [],
  treatment: [],
  where: "",
};

type TStore = TStoreValues<TFilter>;

/**
 * Runtime memory object to handle different stores per group
 *
 * @description Instead of editing persist memory (due to hydration issues),
 * the way to handle different stores without rerendering the store is by
 * storing them in runtime memory.
 */
// eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
const _stores: Record<string, UseBoundStore<StoreApi<TStore>>> = {};

const findingFiltersStore = (
  groupName: string,
): UseBoundStore<StoreApi<TStore>> => {
  if (groupName in _stores) return _stores[groupName];

  const newStore = createWithEqualityFn<TStore>()(
    persist((): TStore => ({ ...initialValues }), {
      name: `findings_filters-${groupName}`,
      storage: createJSONStorage(
        (): StateStorage => filtersStorage as StateStorage,
      ),
    }),
  );

  // eslint-disable-next-line functional/immutable-data
  _stores[groupName] = newStore;

  return newStore;
};

/**
 * Returns the finding filters store for a given group.
 *
 * @param groupName - The group name
 * @returns The finding filters store
 */
const useGetFindingFiltersStore = (groupName: string): TStore => {
  return findingFiltersStore(groupName)((state): TStore => state);
};

const updateFindingFilterStore = (
  groupName: string,
  newFilter: TFlatten<FindingFiltersInput>,
): void => {
  findingFiltersStore(groupName).setState({
    ...findingFiltersStore(groupName).getState(),
    ...newFilter,
  });
};

/**
 * Update the finding filters store using a IFilterOptions array.
 *
 * @param groupName - The group name
 * @param newFilters - The new filters
 */
const updateFindingFiltersStore = (
  groupName: string,
  newFilters: readonly IFilterOptions<FindingFiltersInput>[],
): void => {
  const assignees = formatChecks(getFilter(newFilters, "assignees")?.options)
    ?.split(",")
    .map((value): string => value.trim());

  const titleValues = formatChecks(getFilter(newFilters, "title")?.options)
    ?.split(",")
    .map((value): string => value.trim());

  const reattackValues = formatChecks(
    getFilter(newFilters, "reattack")?.options,
  )
    ?.split(",")
    .map((value): string => value.trim());

  const newWhere = getFilter(newFilters, "where")?.value ?? undefined;
  const search = getFilter(newFilters, "search")?.value ?? undefined;

  const techniqueValues = formatChecks(
    getFilter(newFilters, "technique")?.options,
  )
    ?.split(",")
    .map((value): Technique => value.trim() as Technique);

  const status = formatChecks(getFilter(newFilters, "state")?.options)
    ?.split(",")
    .map((value): FindingStatus => value.trim() as FindingStatus);

  const origin = formatChecks(getFilter(newFilters, "origin")?.options)
    ?.split(",")
    .map((value): VulnerabilityOrigin => value.trim() as VulnerabilityOrigin);

  const treatment = formatChecks(getFilter(newFilters, "treatment")?.options)
    ?.split(",")
    .map((value): string => value.trim());

  const tags = formatChecks(getFilter(newFilters, "tags")?.options)
    ?.split(",")
    .map((value): string => value.trim());

  const severity = formatChecks(
    getFilter(newFilters, "severityRating")?.options,
  )
    ?.split(",")
    .map((value): string => value.trim());

  const fixable = formatChecks(getFilter(newFilters, "autoFixable")?.options);

  const maxSeverity = Number(getFilter(newFilters, "maxSeverity")?.maxValue);
  const minSeverity = Number(getFilter(newFilters, "maxSeverity")?.minValue);
  const maxPriority = Number(getFilter(newFilters, "maxPriority")?.maxValue);
  const minPriority = Number(getFilter(newFilters, "maxPriority")?.minValue);
  const lrInterval = formatChecks(
    getFilter(newFilters, "lastReportInterval")?.options,
  );
  const lastReportAfter = getFilter(newFilters, "lastReportAfter")?.minValue;
  const lastReportBefore = getFilter(newFilters, "lastReportAfter")?.maxValue;

  const reportAfter = getFilter(newFilters, "reportedAfter")?.minValue;
  const reportBefore = getFilter(newFilters, "reportedAfter")?.maxValue;
  const clearSearch =
    search === undefined && titleValues === undefined ? { search } : {};

  findingFiltersStore(groupName).setState({
    assignees,
    autoFixable: fixable === undefined ? undefined : fixable === "true",
    lastReportAfter: lastReportAfter as string,
    lastReportBefore: lastReportBefore as string,
    lastReportInterval: lrInterval as FindingLastReportInterval,
    maxPriority,
    maxSeverity,
    minPriority,
    minSeverity,
    origin: origin?.[0] as unknown as VulnerabilityOrigin,
    reattack: reattackValues as unknown as string[],
    reportedAfter: reportAfter as string,
    reportedBefore: reportBefore as string,
    severityRating: severity,
    state: status as unknown as FindingStatus[],
    tags: tags as unknown as string[],
    technique: techniqueValues as unknown as Technique[],
    title: titleValues as unknown as string[],
    treatment: treatment as unknown as VulnerabilityTreatment[],
    where: newWhere,
    ...clearSearch,
  });
};

/**
 * Reset the finding filters store for a given group.
 *
 * @param groupName - The group name
 */
const resetFindingFiltersStore = (groupName: string): void => {
  findingFiltersStore(groupName).setState(initialValues);
};

/**
 * Reset a specific filter in the finding filters store for a given group.
 *
 * @param groupName - The group name
 * @param key - The key of the filter to reset
 */
const resetFilterFindingFiltersStore = (
  groupName: string,
  key: keyof TFilter,
): void => {
  findingFiltersStore(groupName).setState({
    [key]: initialValues[key],
  });
};

export {
  resetFilterFindingFiltersStore,
  resetFindingFiltersStore,
  useGetFindingFiltersStore,
  updateFindingFiltersStore,
  updateFindingFilterStore,
};
